import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, ClipboardCheck, ChevronRight, CheckCircle2, XCircle,
  AlertTriangle, Clock, Upload, X, ArrowLeft, ExternalLink, Camera,
  Trash2, FileText, ShieldCheck, RotateCcw, Download, RefreshCw, Bug,
  History as HistoryIcon, ListChecks, Sparkles, Filter,
  Database, GitBranch, Rocket, ClipboardList, Save, ShieldAlert,
  Zap, Users, BarChart3,
} from "lucide-react";

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
const STATUS_META = {
  pass:             { label: "Pass",           color: "#059669", bg: "#D1FAE5", icon: CheckCircle2 },
  fail:             { label: "Fail",           color: "#DC2626", bg: "#FEE2E2", icon: XCircle },
  partial:          { label: "Partial",        color: "#B45309", bg: "#FEF3C7", icon: AlertTriangle },
  retest_required:  { label: "Retest Required", color: "#7C3AED", bg: "#EDE9FE", icon: RefreshCw },
  skipped:          { label: "Skipped",        color: "#4B5563", bg: "#E5E7EB", icon: ChevronRight },
  pending:          { label: "Pending",        color: "#64748B", bg: "#F1F5F9", icon: Clock },
};
const SEVERITIES = ["S1", "S2", "S3", "S4"];
const SEVERITY_META = {
  S1: { label: "S1 Blocker",  color: "#991B1B", bg: "#FEE2E2" },
  S2: { label: "S2 Critical", color: "#B45309", bg: "#FEF3C7" },
  S3: { label: "S3 Major",    color: "#1E40AF", bg: "#DBEAFE" },
  S4: { label: "S4 Minor",    color: "#4B5563", bg: "#E5E7EB" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  const Icon = m.icon;
  return (
    <span className="hw-uat-cycles__pill" style={{ color: m.color, background: m.bg }}
          data-testid={`uat-test-status-pill-${status}`}>
      <Icon className="w-3 h-3" />{m.label}
    </span>
  );
}
function SeverityPill({ severity }) {
  const m = SEVERITY_META[severity] || SEVERITY_META.S3;
  return <span className="hw-uat-cycles__pill" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
}

function relDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

// ------------------------------------------------------------------
// ExecutionGuidePanel — renders the structured "How to Execute" guide
// for security tests (Objective / Preconditions / Steps / Expected /
// Pass Criteria / Failure Criteria). Handles range test-ids by
// rendering each sub-test as a collapsible section.
// ------------------------------------------------------------------
function GuideList({ items, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <ul className="hw-uat-cycles__guide-list" data-testid={testid}>
      {items.map((it, idx) => (
        <li key={idx} className="hw-uat-cycles__guide-item">
          <GuideRichText text={it} />
        </li>
      ))}
    </ul>
  );
}

/**
 * Renders bullet text that may include a fenced ```code block```.
 * Everything outside fences renders as inline markdown-ish text; fenced
 * blocks render as a monospaced <pre>.
 */
function GuideRichText({ text }) {
  if (!text) return null;
  const parts = [];
  let inFence = false;
  let buf = [];
  const flush = () => {
    if (buf.length === 0) return;
    if (inFence) {
      parts.push({ type: "code", text: buf.join("\n") });
    } else {
      parts.push({ type: "text", text: buf.join("\n") });
    }
    buf = [];
  };
  for (const line of text.split("\n")) {
    const s = line.trim();
    if (s.startsWith("```")) {
      flush();
      inFence = !inFence;
      continue;
    }
    buf.push(line);
  }
  flush();
  return (
    <>
      {parts.map((p, i) =>
        p.type === "code" ? (
          <pre key={i} className="hw-uat-cycles__guide-code">{p.text}</pre>
        ) : (
          <span key={i} className="hw-uat-cycles__guide-inline">{p.text}</span>
        )
      )}
    </>
  );
}

function SubTestGuide({ sub, index }) {
  const [open, setOpen] = useState(index === 0);
  const anyFail = (sub.failure_criteria || []).length > 0;
  return (
    <div className="hw-uat-cycles__guide-sub" data-testid={`uat-guide-sub-${sub.test_id}`}>
      <button
        type="button"
        className="hw-uat-cycles__guide-sub-header"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`uat-guide-sub-toggle-${sub.test_id}`}
      >
        <ChevronRight
          className="w-4 h-4 hw-uat-cycles__guide-chevron"
          style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s ease" }}
        />
        <strong>{sub.test_id}</strong>
        <span className="hw-uat-cycles__guide-sub-title">{sub.title}</span>
        {sub.severity && (
          <span className="hw-uat-cycles__pill" style={{ marginLeft: "auto", background: "#F1F5F9", color: "#0F172A" }}>
            {sub.severity}
          </span>
        )}
      </button>
      {open && (
        <div className="hw-uat-cycles__guide-sub-body">
          {sub.objective && (
            <div className="hw-uat-cycles__guide-section" data-testid={`uat-guide-objective-${sub.test_id}`}>
              <div className="hw-uat-cycles__guide-section-label">Objective</div>
              <div className="hw-uat-cycles__guide-section-text"><GuideRichText text={sub.objective} /></div>
            </div>
          )}
          {sub.preconditions && sub.preconditions.length > 0 && (
            <div className="hw-uat-cycles__guide-section">
              <div className="hw-uat-cycles__guide-section-label">Preconditions</div>
              <GuideList items={sub.preconditions} testid={`uat-guide-preconditions-${sub.test_id}`} />
            </div>
          )}
          {sub.steps && sub.steps.length > 0 && (
            <div className="hw-uat-cycles__guide-section">
              <div className="hw-uat-cycles__guide-section-label">Steps</div>
              <ol className="hw-uat-cycles__guide-list hw-uat-cycles__guide-list--ordered"
                  data-testid={`uat-guide-steps-${sub.test_id}`}>
                {sub.steps.map((it, i) => (
                  <li key={i} className="hw-uat-cycles__guide-item"><GuideRichText text={it} /></li>
                ))}
              </ol>
            </div>
          )}
          {sub.expected && (
            <div className="hw-uat-cycles__guide-section" data-testid={`uat-guide-expected-${sub.test_id}`}>
              <div className="hw-uat-cycles__guide-section-label">Expected Result</div>
              <div className="hw-uat-cycles__guide-section-text"><GuideRichText text={sub.expected} /></div>
            </div>
          )}
          {sub.pass_criteria && sub.pass_criteria.length > 0 && (
            <div className="hw-uat-cycles__guide-section hw-uat-cycles__guide-section--pass">
              <div className="hw-uat-cycles__guide-section-label">Pass Criteria</div>
              <GuideList items={sub.pass_criteria} testid={`uat-guide-pass-${sub.test_id}`} />
            </div>
          )}
          {anyFail && (
            <div className="hw-uat-cycles__guide-section hw-uat-cycles__guide-section--fail">
              <div className="hw-uat-cycles__guide-section-label">Failure Criteria</div>
              <GuideList items={sub.failure_criteria} testid={`uat-guide-fail-${sub.test_id}`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExecutionGuidePanel({ guide }) {
  if (!guide || !guide.sub_tests || guide.sub_tests.length === 0) return null;
  const isRange = guide.kind === "range";
  return (
    <div className="hw-uat-cycles__guide" data-testid="uat-execution-guide">
      <div className="hw-uat-cycles__guide-header">
        <ListChecks className="w-3.5 h-3.5" />
        <span className="hw-uat-cycles__guide-title">How to Execute</span>
        {isRange && (
          <span className="hw-uat-cycles__pill"
                style={{ marginLeft: "auto", background: "#EDE9FE", color: "#5B21B6" }}>
            {guide.sub_tests.length} sub-tests
          </span>
        )}
      </div>
      <div className="hw-uat-cycles__guide-body">
        {guide.sub_tests.map((sub, i) => (
          <SubTestGuide key={sub.test_id} sub={sub} index={i} />
        ))}
      </div>
    </div>
  );
}

// ---------- Evidence viewer -----------------------------------------
// Renders the captured request+response for a single HTTP interaction.
// Headers are already redacted server-side.
function EvidencePanel({ evidence, index }) {
  const [open, setOpen] = useState(false);
  if (!evidence) return null;
  const req = evidence.request || {};
  const resp = evidence.response || {};
  return (
    <div className="hw-uat-cycles__evidence" data-testid={`uat-evidence-${index}`}>
      <button
        type="button"
        className="hw-uat-cycles__evidence-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <ChevronRight
          className="w-3.5 h-3.5"
          style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s ease" }}
        />
        <span><strong>Evidence #{index + 1}</strong> · {req.method} <code>{req.url}</code> → HTTP <code>{resp.status_code ?? "?"}</code></span>
      </button>
      {open && (
        <div className="hw-uat-cycles__evidence-body">
          <div className="hw-uat-cycles__evidence-section">
            <div className="hw-uat-cycles__evidence-label">Request</div>
            <div className="hw-uat-cycles__evidence-kv">
              <div><span>Method</span><code>{req.method}</code></div>
              <div><span>URL</span><code>{req.url}</code></div>
            </div>
            {req.headers && Object.keys(req.headers).length > 0 && (
              <>
                <div className="hw-uat-cycles__evidence-sublabel">Headers <em>(sensitive values redacted)</em></div>
                <pre className="hw-uat-cycles__evidence-pre">{Object.entries(req.headers).map(([k, v]) => `${k}: ${v}`).join("\n")}</pre>
              </>
            )}
            {req.body !== null && req.body !== undefined && (
              <>
                <div className="hw-uat-cycles__evidence-sublabel">Body</div>
                <pre className="hw-uat-cycles__evidence-pre">{typeof req.body === "string" ? req.body : JSON.stringify(req.body, null, 2)}</pre>
              </>
            )}
          </div>
          <div className="hw-uat-cycles__evidence-section">
            <div className="hw-uat-cycles__evidence-label">Response</div>
            <div className="hw-uat-cycles__evidence-kv">
              <div><span>Status</span><code>{String(resp.status_code ?? "?")}</code></div>
              {evidence.captured_at && <div><span>Captured</span><code>{evidence.captured_at}</code></div>}
            </div>
            {resp.headers && Object.keys(resp.headers).length > 0 && (
              <>
                <div className="hw-uat-cycles__evidence-sublabel">Headers</div>
                <pre className="hw-uat-cycles__evidence-pre">{Object.entries(resp.headers).slice(0, 12).map(([k, v]) => `${k}: ${v}`).join("\n")}</pre>
              </>
            )}
            {resp.body_preview && (
              <>
                <div className="hw-uat-cycles__evidence-sublabel">Body preview</div>
                <pre className="hw-uat-cycles__evidence-pre">{resp.body_preview}</pre>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EvidenceGroup({ evidence }) {
  if (!evidence || evidence.length === 0) return null;
  return (
    <div className="hw-uat-cycles__evidence-group" data-testid="uat-evidence-group">
      <div className="hw-uat-cycles__evidence-group-label">
        <FileText className="w-3.5 h-3.5" /> Evidence ({evidence.length})
      </div>
      {evidence.map((ev, i) => (
        <EvidencePanel key={ev.id || i} evidence={ev} index={i} />
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// Stats banner
// ------------------------------------------------------------------
function StatsBanner({ summary }) {
  if (!summary) return null;
  const { total, passed, failed, partial, retest_required = 0, skipped = 0,
          remaining, completion_pct, passed_pct, estimated_completion_hours, gate } = summary;
  return (
    <div className="hw-uat-cycles__stats" data-testid="uat-cycle-stats">
      <div className="hw-uat-cycles__stat">
        <div className="hw-uat-cycles__stat-num" data-testid="uat-stat-total">{total}</div>
        <div className="hw-uat-cycles__stat-label">Total</div>
      </div>
      <div className="hw-uat-cycles__stat" style={{ color: "#059669" }}>
        <div className="hw-uat-cycles__stat-num" data-testid="uat-stat-passed">{passed}</div>
        <div className="hw-uat-cycles__stat-label">Passed</div>
      </div>
      <div className="hw-uat-cycles__stat" style={{ color: "#DC2626" }}>
        <div className="hw-uat-cycles__stat-num" data-testid="uat-stat-failed">{failed}</div>
        <div className="hw-uat-cycles__stat-label">Failed</div>
      </div>
      <div className="hw-uat-cycles__stat" style={{ color: "#B45309" }}>
        <div className="hw-uat-cycles__stat-num">{partial}</div>
        <div className="hw-uat-cycles__stat-label">Partial</div>
      </div>
      <div className="hw-uat-cycles__stat" style={{ color: "#7C3AED" }}>
        <div className="hw-uat-cycles__stat-num" data-testid="uat-stat-retest">{retest_required}</div>
        <div className="hw-uat-cycles__stat-label">Re-test</div>
      </div>
      <div className="hw-uat-cycles__stat">
        <div className="hw-uat-cycles__stat-num" data-testid="uat-stat-skipped">{skipped}</div>
        <div className="hw-uat-cycles__stat-label">Skipped</div>
      </div>
      <div className="hw-uat-cycles__stat">
        <div className="hw-uat-cycles__stat-num" data-testid="uat-stat-remaining">{remaining}</div>
        <div className="hw-uat-cycles__stat-label">Remaining</div>
      </div>
      <div className="hw-uat-cycles__stat hw-uat-cycles__stat--wide">
        <div className="hw-uat-cycles__progress" data-testid="uat-completion-bar">
          <div className="hw-uat-cycles__progress-bar" style={{ width: `${completion_pct}%` }} />
        </div>
        <div className="hw-uat-cycles__stat-label" style={{ marginTop: 6 }}>
          <strong data-testid="uat-completion-pct">{completion_pct}%</strong> complete ·
          <strong data-testid="uat-pass-pct"> {passed_pct}%</strong> pass ·
          <span data-testid="uat-est-time"> est. {estimated_completion_hours}h remaining</span>
        </div>
      </div>
      <div className={"hw-uat-cycles__gate " + (gate?.ready_for_signoff ? "is-ok" : "is-blocked")}
           data-testid="uat-gate-banner">
        {gate?.ready_for_signoff
          ? (<><ShieldCheck className="w-4 h-4" /> Ready for sign-off</>)
          : (<><AlertTriangle className="w-4 h-4" /> Sign-off gate blocked</>)}
      </div>
    </div>
  );
}

// A compact list of what still needs to happen before sign-off is possible.
// Shows the 12-point strict Final Release Gate.
function GateBreakdown({ gate }) {
  if (!gate) return null;
  const items = [
    { key: "no_open_s1_s2_defects",           label: `Zero open S1/S2 defects (${gate.open_blocker_defects || 0} open)` },
    { key: "all_required_tests_passed",       label: `All required tests passed (${gate.required_tests_passed || 0}/${gate.required_tests_total || 0} S1+S2+regression)` },
    { key: "all_automated_checks_passed",     label: `All automated checks passed (${gate.automated_checks_passed || 0}/${gate.automated_checks_total || 0})` },
    { key: "no_pending_tests",                label: "No pending / retest tests" },
    { key: "prod_readiness_complete",         label: `Production Readiness Checklist complete (${gate.prod_readiness_done_count || 0}/${gate.prod_readiness_total || 0})` },
    { key: "overall_pass_rate_ok",            label: "≥ 95% overall pass rate" },
    { key: "summary_report_exported",         label: "UAT Summary Report exported" },
    { key: "release_notes_finalized",         label: "Release notes finalized" },
    { key: "db_backup_verified_recent",       label: gate.last_backup_age_hours != null
                                                     ? `DB backup verified within 24h (last: ${gate.last_backup_age_hours}h ago)`
                                                     : "DB backup verified within 24h" },
    { key: "rollback_plan_validated",         label: "Rollback runbook validated" },
    { key: "monitoring_and_audit_configured", label: "Monitoring & audit configured" },
    { key: "release_metadata_captured",       label: "Release metadata captured" },
  ];
  return (
    <div className="hw-uat-cycles__gate-list" data-testid="uat-gate-breakdown">
      <div className="hw-uat-cycles__gate-title" data-testid="uat-gate-title">
        <ShieldAlert className="w-4 h-4" />
        Final Release Gate — {gate.strict_checks_passed || 0}/{gate.strict_checks_total || 12} strict checks passed
      </div>
      {items.map((it) => (
        <div key={it.key} className={`hw-uat-cycles__gate-item${gate[it.key] ? " is-ok" : ""}`}
             data-testid={`uat-gate-check-${it.key}`}
             data-passed={gate[it.key] ? "true" : "false"}>
          {gate[it.key] ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
          {it.label}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------
// Test row & drawer
// ------------------------------------------------------------------
function TestRow({ test, onOpen }) {
  const autoStatus = test.automation_status || "manual_only";
  const autoMeta = {
    runner_available: { label: "AUTO", color: "#065F46", bg: "#D1FAE5" },
    runner_pending:   { label: "AUTO*", color: "#B45309", bg: "#FEF3C7" },
    manual_only:      { label: "MANUAL", color: "#7C3AED", bg: "#EDE9FE" },
  }[autoStatus] || { label: "MANUAL", color: "#7C3AED", bg: "#EDE9FE" };
  return (
    <button type="button"
            className={`hw-uat-cycles__row hw-uat-cycles__row--${test.status}`}
            onClick={() => onOpen(test)}
            data-testid={`uat-test-row-${test.test_id}`}
            data-automation={autoStatus}>
      <span className="hw-uat-cycles__row-id">{test.test_id}</span>
      <span className="hw-uat-cycles__row-title" title={test.title}>{test.title}</span>
      <SeverityPill severity={test.severity} />
      <span className="hw-uat-cycles__pill"
            style={{ color: autoMeta.color, background: autoMeta.bg }}
            title={test.category_reason || autoStatus}
            data-testid={`uat-auto-pill-${test.test_id}`}>
        {autoMeta.label}
      </span>
      <StatusPill status={test.status || "pending"} />
      {test.defect_id && (
        <span className="hw-uat-cycles__row-attach" title="Linked defect"><Bug className="w-3 h-3" /></span>
      )}
      {test.screenshots?.length > 0 && (
        <span className="hw-uat-cycles__row-attach"><Camera className="w-3 h-3" />{test.screenshots.length}</span>
      )}
    </button>
  );
}

function TestDrawer({ cycleId, test, isSignedOff, onClose, onUpdated }) {
  const [status, setStatus] = useState(test.status || "pending");
  const [severity, setSeverity] = useState(test.severity || "S3");
  const [notes, setNotes] = useState(test.notes || "");
  const [actual, setActual] = useState(test.actual_result || "");
  const [screenLink, setScreenLink] = useState(test.affected_screen_url || "");
  const [assignedTester, setAssignedTester] = useState(test.assigned_tester || "");
  const [assignedDeveloper, setAssignedDeveloper] = useState(test.assigned_developer || "");
  const [linkedDefect, setLinkedDefect] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [screenshots, setScreenshots] = useState(test.screenshots || []);
  const fileRef = useRef(null);
  const readOnly = !!isSignedOff;

  useEffect(() => {
    setStatus(test.status || "pending"); setSeverity(test.severity || "S3");
    setNotes(test.notes || ""); setActual(test.actual_result || "");
    setScreenLink(test.affected_screen_url || ""); setScreenshots(test.screenshots || []);
    setAssignedTester(test.assigned_tester || "");
    setAssignedDeveloper(test.assigned_developer || "");
    // Fetch linked defect (if any)
    if (test.defect_id) {
      api.get(`/admin/uat-cycles/${cycleId}/defects/${test.defect_id}`)
        .then((r) => setLinkedDefect(r.data))
        .catch(() => setLinkedDefect(null));
    } else {
      setLinkedDefect(null);
    }
  }, [test, cycleId]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/uat-cycles/${cycleId}/tests/${test.test_id}`,
        {
          status, severity, notes,
          actual_result: actual,
          affected_screen_url: screenLink,
          assigned_tester: assignedTester,
          assigned_developer: assignedDeveloper,
        }
      );
      toast.success("Test updated");
      if (data.defect) setLinkedDefect(data.defect);
      onUpdated(data.summary, data.test);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Screenshot must be < 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/admin/uat-cycles/${cycleId}/tests/${test.test_id}/screenshots`,
        fd, { headers: { "Content-Type": "multipart/form-data" } }
      );
      setScreenshots((s) => [...s, data]);
      toast.success("Screenshot uploaded");
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeShot = async (ssId) => {
    try {
      await api.delete(`/admin/uat-cycles/${cycleId}/tests/${test.test_id}/screenshots/${ssId}`);
      setScreenshots((s) => s.filter((x) => x.id !== ssId));
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  // Automated check runner state (only meaningful when test.runnable === true).
  const [running, setRunning] = useState(false);
  const [autoRuns, setAutoRuns] = useState(test.automated_runs || []);
  const lastRun = autoRuns.length > 0 ? autoRuns[autoRuns.length - 1] : null;

  useEffect(() => {
    setAutoRuns(test.automated_runs || []);
  }, [test]);

  const runCheck = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(
        `/admin/uat-cycles/${cycleId}/tests/${test.test_id}/run-check`
      );
      setAutoRuns((prev) => [...prev, data.result]);
      const passed = data.result.passed;
      toast[passed ? "success" : "error"](
        passed
          ? `Automated check PASSED (${data.result.actual_status})`
          : `Automated check FAILED (${data.result.actual_status})`,
      );
    } catch (e) {
      toast.error(formatApiError(e, "Runner failed"));
    } finally {
      setRunning(false);
    }
  };

  const applyRunResult = () => {
    if (!lastRun) return;
    setStatus(lastRun.passed ? "pass" : "fail");
    if (!actual) {
      setActual(
        `[Automated check]\n${lastRun.details || ""}\n` +
        `HTTP ${lastRun.actual_status} on ${lastRun.endpoint}`
      );
    }
    toast.info(`Status set to ${lastRun.passed ? "Pass" : "Fail"} — click Save changes to persist.`);
  };

  const token = localStorage.getItem("hw_access_token");
  const shotUrl = (ssId) =>
    `${api.defaults.baseURL}/admin/uat-cycles/${cycleId}/tests/${test.test_id}/screenshots/${ssId}`;

  const fetchShotAsBlob = async (ssId) => {
    const r = await fetch(shotUrl(ssId), { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return URL.createObjectURL(await r.blob());
  };

  return (
    <div className="hw-uat-cycles__drawer" role="dialog" aria-label="Test details"
         data-testid="uat-test-drawer">
      <div className="hw-uat-cycles__drawer-header">
        <div>
          <div className="hw-uat-cycles__drawer-crumb">{test.category} · {test.area_title}</div>
          <div className="hw-uat-cycles__drawer-title">
            <code>{test.test_id}</code> — {test.title}
          </div>
        </div>
        <button type="button" className="hw-uat-cycles__icon-btn"
                onClick={onClose} data-testid="uat-drawer-close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="hw-uat-cycles__drawer-body">
        {readOnly && (
          <div className="hw-uat-cycles__hint" style={{ padding: "0.5rem 0.7rem", background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: "0.5rem", color: "#92400E" }}
               data-testid="uat-drawer-readonly-notice">
            This cycle is signed off — read-only. Use <strong>Reopen</strong> on the cycle to make changes.
          </div>
        )}

        {test.expected_result && (
          <div className="hw-uat-cycles__expected" data-testid="uat-expected-result">
            <div className="hw-uat-cycles__expected-label"><Sparkles className="w-3.5 h-3.5" /> Expected Result</div>
            <pre className="hw-uat-cycles__expected-body">{test.expected_result}</pre>
          </div>
        )}

        <ExecutionGuidePanel guide={test.execution_guide} />

        {test.runnable && (
          <div className="hw-uat-cycles__runner" data-testid="uat-automated-runner">
            <div className="hw-uat-cycles__runner-header">
              <ShieldCheck className="w-4 h-4" />
              <strong>Automated Check Available</strong>
              <button
                type="button"
                className="hw-uat-cycles__runner-btn"
                onClick={runCheck}
                disabled={running || readOnly}
                data-testid="uat-run-check-btn"
              >
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                {running ? "Running…" : (autoRuns.length > 0 ? "Re-run Check" : "Run Automated Check")}
              </button>
            </div>
            <p className="hw-uat-cycles__runner-help">
              This test can be executed automatically by the backend — logs in as a volunteer,
              hits the endpoint, and verifies the HTTP response. Result appears below;
              click <em>Apply Result to Status</em> to auto-fill Pass/Fail.
            </p>

            {lastRun && (
              <div
                className={`hw-uat-cycles__runner-result hw-uat-cycles__runner-result--${lastRun.passed ? "pass" : "fail"}`}
                data-testid={`uat-run-result-${lastRun.passed ? "pass" : "fail"}`}
              >
                <div className="hw-uat-cycles__runner-result-header">
                  {lastRun.passed ? (
                    <CheckCircle2 className="w-4 h-4" style={{ color: "#059669" }} />
                  ) : (
                    <XCircle className="w-4 h-4" style={{ color: "#DC2626" }} />
                  )}
                  <strong>{lastRun.passed ? "PASS" : "FAIL"}</strong>
                  <span className="hw-uat-cycles__runner-result-meta">
                    HTTP <code>{String(lastRun.actual_status)}</code>
                    {" "}(expected <code>{Array.isArray(lastRun.expected_status)
                      ? lastRun.expected_status.join("/")
                      : String(lastRun.expected_status)}</code>)
                  </span>
                  <span className="hw-uat-cycles__runner-result-meta">
                    · <code>{lastRun.endpoint}</code>
                  </span>
                  <span className="hw-uat-cycles__runner-result-meta">
                    · as <strong>{lastRun.actor}</strong>
                  </span>
                  <span className="hw-uat-cycles__runner-result-meta" style={{ marginLeft: "auto" }}>
                    {lastRun.duration_ms} ms · {relDate(lastRun.executed_at)}
                  </span>
                </div>
                {lastRun.details && (
                  <pre className="hw-uat-cycles__runner-result-details">{lastRun.details}</pre>
                )}
                {lastRun.sub_results && lastRun.sub_results.length > 0 && (
                  <ul className="hw-uat-cycles__runner-sub-list">
                    {lastRun.sub_results.map((sr, i) => (
                      <li key={i} className={`hw-uat-cycles__runner-sub-item hw-uat-cycles__runner-sub-item--${
                        sr.skipped ? "skip" : sr.passed ? "pass" : "fail"
                      }`}>
                        <div className="hw-uat-cycles__runner-sub-row">
                          <strong>{sr.test_id}</strong>
                          <span>
                            {sr.skipped ? "SKIP · no runner"
                              : sr.passed ? `PASS · HTTP ${sr.actual_status}`
                              : `FAIL · HTTP ${sr.actual_status}`}
                          </span>
                        </div>
                        {sr.details && !sr.skipped && (
                          <pre className="hw-uat-cycles__runner-sub-details">{sr.details}</pre>
                        )}
                        {sr.evidence && sr.evidence.length > 0 && (
                          <EvidenceGroup evidence={sr.evidence} />
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <EvidenceGroup evidence={lastRun.evidence} />
                <button
                  type="button"
                  className="hw-uat-cycles__runner-apply-btn"
                  onClick={applyRunResult}
                  disabled={readOnly}
                  data-testid="uat-apply-run-result-btn"
                >
                  {lastRun.passed ? "Approve Pass" : "Approve Fail"} · Apply to Status
                </button>
                {autoRuns.length > 1 && (
                  <div className="hw-uat-cycles__runner-history-note">
                    {autoRuns.length} runs on record — showing the most recent.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {linkedDefect && (
          <div className={`hw-uat-cycles__defect-card hw-uat-cycles__defect-card--${linkedDefect.status}`}
               data-testid="uat-linked-defect">
            <div className="hw-uat-cycles__defect-header">
              <Bug className="w-4 h-4" />
              <strong>Linked defect</strong>
              <span className="hw-uat-cycles__pill" style={{ marginLeft: "auto", background: "#FEE2E2", color: "#991B1B" }}>
                {linkedDefect.severity}
              </span>
              <span className="hw-uat-cycles__pill" style={{ background: "#F1F5F9", color: "#0F172A", textTransform: "capitalize" }}>
                {linkedDefect.status?.replace(/_/g, " ")}
              </span>
            </div>
            <div className="hw-uat-cycles__defect-meta">
              Opened {relDate(linkedDefect.opened_at)}
              {linkedDefect.assigned_owner && <> · Assigned to <strong>{linkedDefect.assigned_owner}</strong></>}
              {linkedDefect.date_fixed && <> · Fixed {relDate(linkedDefect.date_fixed)}</>}
              {linkedDefect.date_retested && <> · Re-tested {relDate(linkedDefect.date_retested)}</>}
            </div>
            {linkedDefect.resolution_notes && (
              <div className="hw-uat-cycles__defect-notes">Resolution: {linkedDefect.resolution_notes}</div>
            )}
          </div>
        )}

        <div className="hw-uat-cycles__row-group">
          <label>Status</label>
          <div className="hw-uat-cycles__status-group">
            {Object.keys(STATUS_META).map((s) => (
              <button key={s} type="button"
                      className={`hw-uat-cycles__status-btn${status === s ? " is-active" : ""}`}
                      style={{ "--pill": STATUS_META[s].color }}
                      onClick={() => setStatus(s)}
                      disabled={readOnly}
                      data-testid={`uat-set-status-${s}`}>
                {STATUS_META[s].label}
              </button>
            ))}
          </div>
        </div>

        <div className="hw-uat-cycles__row-group">
          <label>Severity if failed</label>
          <div className="hw-uat-cycles__status-group">
            {SEVERITIES.map((s) => (
              <button key={s} type="button"
                      className={`hw-uat-cycles__status-btn${severity === s ? " is-active" : ""}`}
                      onClick={() => setSeverity(s)}
                      data-testid={`uat-set-sev-${s}`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="hw-uat-cycles__row-group">
          <label htmlFor="uat-actual">Actual result</label>
          <textarea id="uat-actual" rows={2} value={actual}
                    onChange={(e) => setActual(e.target.value)}
                    placeholder="What you observed…"
                    data-testid="uat-actual-input" />
        </div>

        <div className="hw-uat-cycles__row-group">
          <label htmlFor="uat-notes">Tester notes</label>
          <textarea id="uat-notes" rows={3} value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Reproduction steps, observations, blockers…"
                    data-testid="uat-notes-input" />
        </div>

        <div className="hw-uat-cycles__row-group">
          <label htmlFor="uat-link">Link to affected screen</label>
          <div className="hw-uat-cycles__link-row">
            <input id="uat-link" type="url" value={screenLink}
                   onChange={(e) => setScreenLink(e.target.value)}
                   placeholder="e.g. /admin/compliance or full URL"
                   data-testid="uat-link-input" />
            {screenLink && (
              <a className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                 href={screenLink.startsWith("http") ? screenLink : screenLink}
                 target={screenLink.startsWith("http") ? "_blank" : "_self"}
                 rel="noopener noreferrer"
                 data-testid="uat-link-open">
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </a>
            )}
          </div>
        </div>

        <div className="hw-uat-cycles__two-col">
          <div className="hw-uat-cycles__row-group">
            <label htmlFor="uat-tester">Assigned tester</label>
            <input id="uat-tester" type="text" value={assignedTester}
                   onChange={(e) => setAssignedTester(e.target.value)}
                   placeholder="Name or email"
                   disabled={readOnly}
                   data-testid="uat-tester-input" />
          </div>
          <div className="hw-uat-cycles__row-group">
            <label htmlFor="uat-dev">Assigned developer</label>
            <input id="uat-dev" type="text" value={assignedDeveloper}
                   onChange={(e) => setAssignedDeveloper(e.target.value)}
                   placeholder="Name or email"
                   disabled={readOnly}
                   data-testid="uat-dev-input" />
          </div>
        </div>

        <div className="hw-uat-cycles__row-group">
          <label>Screenshots ({screenshots.length})</label>
          <div className="hw-uat-cycles__shots">
            {screenshots.map((s) => (
              <ScreenshotThumb key={s.id} ss={s} fetchUrl={() => fetchShotAsBlob(s.id)}
                               onRemove={() => removeShot(s.id)} />
            ))}
            <label className="hw-uat-cycles__shot-add" data-testid="uat-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4" /> Upload</>}
              <input ref={fileRef} type="file" accept="image/*"
                     onChange={(e) => upload(e.target.files?.[0])} hidden
                     data-testid="uat-upload-input" />
            </label>
          </div>
          <div className="hw-uat-cycles__hint">PNG / JPG / WebP up to 5 MB.</div>
        </div>
      </div>

      <div className="hw-uat-cycles__drawer-footer">
        <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                onClick={onClose} data-testid="uat-drawer-cancel">Cancel</button>
        <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                disabled={saving} onClick={save} data-testid="uat-save-test">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
        </button>
      </div>
    </div>
  );
}

function ScreenshotThumb({ ss, fetchUrl, onRemove }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false, obj;
    fetchUrl().then((u) => { if (!cancelled) { obj = u; setUrl(u); } }).catch(() => {});
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
  }, [ss.id, fetchUrl]);
  return (
    <div className="hw-uat-cycles__shot">
      {url ? <img src={url} alt={ss.filename || ""} /> : <Loader2 className="w-4 h-4 animate-spin" />}
      <button type="button" className="hw-uat-cycles__shot-remove"
              onClick={onRemove} aria-label="Remove screenshot"
              data-testid={`uat-shot-remove-${ss.id}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ------------------------------------------------------------------
// Production Readiness Checklist
// ------------------------------------------------------------------
function ProdReadinessPanel({ cycleId, items, isSignedOff, onUpdated }) {
  const toggle = async (item) => {
    try {
      const { data } = await api.post(
        `/admin/uat-cycles/${cycleId}/prod-readiness/${item.id}/toggle`,
        { done: !item.done }
      );
      toast.success(item.done ? "Unchecked" : "Marked done");
      onUpdated(data.production_readiness, data.summary);
    } catch (e) {
      toast.error(formatApiError(e, "Toggle failed"));
    }
  };
  const done = items.filter((i) => i.done).length;
  return (
    <div className="hw-uat-cycles__panel" data-testid="uat-prod-readiness">
      <div className="hw-uat-cycles__panel-header">
        <ListChecks className="w-4 h-4" />
        <span>Production Readiness Checklist</span>
        <span className="hw-uat-cycles__panel-count">{done} / {items.length}</span>
      </div>
      <ul className="hw-uat-cycles__checklist">
        {items.map((it) => (
          <li key={it.id}>
            <label className={`hw-uat-cycles__check${it.done ? " is-done" : ""}`}
                   data-testid={`uat-readiness-${it.id}`}>
              <input type="checkbox" checked={!!it.done}
                     disabled={isSignedOff}
                     onChange={() => toggle(it)}
                     data-testid={`uat-readiness-${it.id}-cb`} />
              <span>{it.label}</span>
              {it.done && it.done_at && (
                <span className="hw-uat-cycles__check-meta">— {relDate(it.done_at)}</span>
              )}
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ------------------------------------------------------------------
// Defects panel (per cycle)
// ------------------------------------------------------------------
function DefectsPanel({ cycleId, isSignedOff, onDefectsChanged }) {
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/uat-cycles/${cycleId}/defects`);
      setDefects(data.defects);
      onDefectsChanged?.(data.defects);
    } catch (e) { toast.error(formatApiError(e, "Load defects failed")); }
    finally { setLoading(false); }
  }, [cycleId, onDefectsChanged]);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const { data } = await api.patch(
        `/admin/uat-cycles/${cycleId}/defects/${editing.id}`,
        patch
      );
      toast.success("Defect updated");
      setDefects((prev) => prev.map((d) => d.id === data.id ? data : d));
      setEditing(null);
      onDefectsChanged?.();
    } catch (e) { toast.error(formatApiError(e, "Update failed")); }
  };

  return (
    <div className="hw-uat-cycles__panel" data-testid="uat-defects-panel">
      <div className="hw-uat-cycles__panel-header">
        <Bug className="w-4 h-4" />
        <span>Defects</span>
        <span className="hw-uat-cycles__panel-count">{defects.length}</span>
        <button type="button" className="hw-uat-cycles__icon-btn" onClick={load}
                aria-label="Refresh" data-testid="uat-defects-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? (
        <div className="hw-uat-cycles__hint">Loading…</div>
      ) : defects.length === 0 ? (
        <div className="hw-uat-cycles__hint">No defects yet — failed tests will auto-create defects.</div>
      ) : (
        <ul className="hw-uat-cycles__defect-list">
          {defects.map((d) => (
            <li key={d.id} className={`hw-uat-cycles__defect-item hw-uat-cycles__defect-item--${d.status}`}
                data-testid={`uat-defect-${d.id}`}>
              <div className="hw-uat-cycles__defect-item-row">
                <code className="hw-uat-cycles__defect-tid">{d.test_id}</code>
                <span className="hw-uat-cycles__defect-title">{d.test_title}</span>
                <span className="hw-uat-cycles__pill" style={{ background: "#FEE2E2", color: "#991B1B" }}>{d.severity}</span>
                <span className="hw-uat-cycles__pill" style={{ background: "#F1F5F9", color: "#0F172A", textTransform: "capitalize" }}>{d.status.replace(/_/g, " ")}</span>
                <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                        onClick={() => setEditing(d)} disabled={isSignedOff}
                        data-testid={`uat-defect-edit-${d.id}`}>Edit</button>
              </div>
              <div className="hw-uat-cycles__defect-item-meta">
                Opened {relDate(d.opened_at)}
                {d.assigned_owner && <> · Owner: <strong>{d.assigned_owner}</strong></>}
                {d.date_fixed && <> · Fixed {relDate(d.date_fixed)}</>}
                {d.date_retested && <> · Re-tested {relDate(d.date_retested)}</>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {editing && (
        <DefectEditor defect={editing} onClose={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
}

function DefectEditor({ defect, onClose, onSave }) {
  const [owner, setOwner] = useState(defect.assigned_owner || "");
  const [status, setStatus] = useState(defect.status);
  const [severity, setSeverity] = useState(defect.severity);
  const [notes, setNotes] = useState(defect.resolution_notes || "");
  return (
    <>
      <div className="hw-uat-cycles__scrim" onClick={onClose} />
      <div className="hw-uat-cycles__dialog" data-testid="uat-defect-dialog">
        <div className="hw-uat-cycles__dialog-header">
          <Bug className="w-4 h-4" /> Defect · {defect.test_id}
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-uat-cycles__dialog-body">
          <div className="hw-uat-cycles__row-group">
            <label htmlFor="def-owner">Assigned owner</label>
            <input id="def-owner" type="text" value={owner} onChange={(e) => setOwner(e.target.value)}
                   placeholder="Name or email" data-testid="uat-defect-owner" />
          </div>
          <div className="hw-uat-cycles__row-group">
            <label>Status</label>
            <div className="hw-uat-cycles__status-group">
              {["open","in_progress","fixed","closed","reopened"].map((s) => (
                <button key={s} type="button"
                        className={`hw-uat-cycles__status-btn${status === s ? " is-active" : ""}`}
                        onClick={() => setStatus(s)}
                        data-testid={`uat-defect-status-${s}`}>{s.replace("_"," ")}</button>
              ))}
            </div>
          </div>
          <div className="hw-uat-cycles__row-group">
            <label>Severity</label>
            <div className="hw-uat-cycles__status-group">
              {SEVERITIES.map((s) => (
                <button key={s} type="button"
                        className={`hw-uat-cycles__status-btn${severity === s ? " is-active" : ""}`}
                        onClick={() => setSeverity(s)}>{s}</button>
              ))}
            </div>
          </div>
          <div className="hw-uat-cycles__row-group">
            <label htmlFor="def-notes">Resolution notes</label>
            <textarea id="def-notes" rows={3} value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      data-testid="uat-defect-notes" />
          </div>
        </div>
        <div className="hw-uat-cycles__dialog-footer">
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                  onClick={() => onSave({ assigned_owner: owner, status, severity, resolution_notes: notes })}
                  data-testid="uat-defect-save">Save</button>
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// Cycle detail view
// ------------------------------------------------------------------
function CycleDetail({ cycle, onBack, onCycleChanged }) {
  const [filter, setFilter] = useState({
    status: "all", category: "all", severity: "all", q: "",
    tester: "", developer: "", critical_only: false, retest_only: false,
    failed_only: false, manual_only: false,
  });
  const [openTest, setOpenTest] = useState(null);
  const [showSignoff, setShowSignoff] = useState(false);
  const [showAutoExec, setShowAutoExec] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);
  const [autoExecResult, setAutoExecResult] = useState(null);
  const [current, setCurrent] = useState(cycle);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/uat-cycles/${cycle.id}`);
      setCurrent(data);
      onCycleChanged?.(data);
    } catch (e) { toast.error(formatApiError(e, "Refresh failed")); }
  }, [cycle.id, onCycleChanged]);

  useEffect(() => { setCurrent(cycle); }, [cycle]);

  const onTestUpdated = (summary, test) => {
    setCurrent((c) => ({
      ...c, summary,
      tests: c.tests.map((t) => t.test_id === test.test_id ? test : t),
    }));
    setOpenTest(test);
    onCycleChanged?.({ ...current, summary });
  };

  const filtered = useMemo(() => {
    const tests = current?.tests || [];
    return tests.filter((t) => {
      if (filter.status !== "all" && (t.status || "pending") !== filter.status) return false;
      if (filter.category !== "all" && t.category !== filter.category) return false;
      if (filter.severity !== "all" && t.severity !== filter.severity) return false;
      if (filter.critical_only && !["S1", "S2"].includes(t.severity)) return false;
      if (filter.retest_only && t.status !== "retest_required") return false;
      if (filter.failed_only && !["fail", "partial"].includes(t.status)) return false;
      if (filter.manual_only && t.automation_status !== "manual_only") return false;
      if (filter.tester) {
        const v = filter.tester.toLowerCase();
        if (!(t.assigned_tester || "").toLowerCase().includes(v)) return false;
      }
      if (filter.developer) {
        const v = filter.developer.toLowerCase();
        if (!(t.assigned_developer || "").toLowerCase().includes(v)) return false;
      }
      if (filter.q) {
        const q = filter.q.toLowerCase();
        if (!t.test_id.toLowerCase().includes(q) && !t.title.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [current, filter]);

  const downloadReport = async () => {
    const token = localStorage.getItem("hw_access_token");
    const url = `${api.defaults.baseURL}/admin/uat-cycles/${current.id}/export`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `UAT_Report_${current.release_name.replace(/\s+/g,"_")}.md`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const reopen = async () => {
    if (!window.confirm("Reopen this cycle for editing? The prior sign-off will be preserved in history.")) return;
    try {
      await api.post(`/admin/uat-cycles/${current.id}/reopen`);
      toast.success("Cycle reopened");
      refresh();
    } catch (e) { toast.error(formatApiError(e, "Reopen failed")); }
  };

  // Group filtered tests by category > area for grid rendering.
  const groups = useMemo(() => {
    const out = {};
    filtered.forEach((t) => {
      out[t.category] = out[t.category] || {};
      out[t.category][t.area_title] = out[t.category][t.area_title] || [];
      out[t.category][t.area_title].push(t);
    });
    return out;
  }, [filtered]);

  const isSignedOff = current?.status === "signed_off";
  const ready = current?.summary?.gate?.ready_for_signoff;

  return (
    <div className="hw-uat-cycles__detail" data-testid="uat-cycle-detail">
      <div className="hw-uat-cycles__detail-header">
        <div>
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={onBack} data-testid="uat-detail-back">
            <ArrowLeft className="w-4 h-4" /> All cycles
          </button>
          <h2 data-testid="uat-cycle-name">{current.release_name}</h2>
          <div className="hw-uat-cycles__detail-meta">
            {current.release_version && <span>v{current.release_version}</span>}
            <span>·</span>
            <span>Created {relDate(current.created_at)}</span>
            <span>·</span>
            <span data-testid="uat-cycle-status">Status: <strong>{current.status.replace("_"," ")}</strong></span>
          </div>
        </div>
        <div className="hw-uat-cycles__detail-actions">
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={() => setShowCoverage(true)} data-testid="uat-coverage-report-btn">
            <BarChart3 className="w-4 h-4" /> Coverage Report
          </button>
          {!isSignedOff && (
            <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                    onClick={() => setShowAutoExec(true)} data-testid="uat-auto-execute-btn">
              <Zap className="w-4 h-4" /> Auto Execute
            </button>
          )}
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={downloadReport} data-testid="uat-download-report">
            <Download className="w-4 h-4" /> Export report
          </button>
          {isSignedOff ? (
            <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                    onClick={reopen} data-testid="uat-reopen">
              <RotateCcw className="w-4 h-4" /> Reopen
            </button>
          ) : (
            <button type="button"
                    className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                    onClick={() => setShowSignoff(true)}
                    disabled={!ready}
                    title={ready ? "" : `Final Release Gate blocked — ${current?.summary?.gate?.strict_checks_passed || 0}/${current?.summary?.gate?.strict_checks_total || 12} strict checks passed`}
                    data-testid="uat-signoff-btn">
              <ShieldCheck className="w-4 h-4" /> Sign off
            </button>
          )}
        </div>
      </div>

      <StatsBanner summary={current.summary} />

      {/* Iter 57 — Categorization summary strip */}
      {current.categorization && (
        <div className="hw-uat-cycles__cat-strip" data-testid="uat-cat-strip">
          <div className="hw-uat-cycles__cat-strip-title">
            <Zap className="w-3.5 h-3.5" /> Automation coverage
          </div>
          <div className="hw-uat-cycles__cat-strip-metrics">
            <span data-testid="uat-cat-runner-available">
              <strong>{current.categorization.runner_available}</strong> auto (runner ready)
            </span>
            <span data-testid="uat-cat-runner-pending">
              <strong>{current.categorization.runner_pending}</strong> auto (runner backlog)
            </span>
            <span data-testid="uat-cat-manual-only" style={{ color: "#7C3AED" }}>
              <strong>{current.categorization.manual_only}</strong> manual-only (human review)
            </span>
            <span className="hw-uat-cycles__cat-strip-total">
              of {current.categorization.total} tests
            </span>
          </div>
        </div>
      )}

      {current.provenance && (
        <div className="hw-uat-cycles__provenance" data-testid="uat-cycle-provenance">
          <span className="hw-uat-cycles__provenance-label">Provenance</span>
          <span>Created {relDate(current.provenance.generated_at)}</span>
          {current.provenance.app_version && (<><span>·</span><span>App <code>{current.provenance.app_version}</code></span></>)}
          {current.provenance.git?.commit_short && (
            <><span>·</span><span>Git <code>{current.provenance.git.commit_short}</code> on <code>{current.provenance.git.branch || "main"}</code></span></>
          )}
          {current.provenance.release?.label && (<><span>·</span><span>{current.provenance.release.label}</span></>)}
          {current.provenance.environment && (<><span>·</span><span>{current.provenance.environment}</span></>)}
        </div>
      )}

      {isSignedOff && (
        <div className="hw-uat-cycles__signoff-banner" data-testid="uat-signoff-banner">
          <CheckCircle2 className="w-4 h-4" />
          Signed off by <strong>{current.signoff_signature}</strong> on {relDate(current.signed_off_at)}
          &nbsp;— Decision: <strong>{current.signoff_decision?.replace(/_/g," ")}</strong>
          {current.signoff_conditions && <div className="hw-uat-cycles__signoff-conditions">Conditions: {current.signoff_conditions}</div>}
        </div>
      )}

      {/* Final Release Gate — 12 strict checks + release metadata/rollback/backup */}
      <FinalReleaseGatePanel
        cycle={current}
        isSignedOff={isSignedOff}
        onCycleUpdate={(patch) => setCurrent((c) => ({ ...c, ...patch }))} />

      {/* Production Readiness Checklist */}
      <ProdReadinessPanel
        cycleId={current.id}
        items={current.production_readiness || []}
        isSignedOff={isSignedOff}
        onUpdated={(items, summary) => {
          setCurrent((c) => ({ ...c, production_readiness: items, summary }));
        }} />

      {/* Defects */}
      <DefectsPanel cycleId={current.id} isSignedOff={isSignedOff}
                    onDefectsChanged={refresh} />

      {/* Filters */}
      <div className="hw-uat-cycles__filters no-print">
        <input type="search" placeholder="Search test id or title…"
               value={filter.q} onChange={(e) => setFilter({...filter, q: e.target.value})}
               data-testid="uat-filter-search" />
        <select value={filter.status} onChange={(e) => setFilter({...filter, status: e.target.value})}
                data-testid="uat-filter-status">
          <option value="all">All statuses</option>
          {Object.keys(STATUS_META).map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <select value={filter.severity} onChange={(e) => setFilter({...filter, severity: e.target.value})}
                data-testid="uat-filter-sev">
          <option value="all">All severities</option>
          {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filter.category} onChange={(e) => setFilter({...filter, category: e.target.value})}
                data-testid="uat-filter-cat">
          <option value="all">All categories</option>
          {Object.keys(current?.summary?.per_category || {}).map(c =>
            <option key={c} value={c}>{c}</option>
          )}
        </select>
        <input type="text" placeholder="Tester…" value={filter.tester}
               onChange={(e) => setFilter({...filter, tester: e.target.value})}
               data-testid="uat-filter-tester" style={{ maxWidth: 140 }} />
        <input type="text" placeholder="Developer…" value={filter.developer}
               onChange={(e) => setFilter({...filter, developer: e.target.value})}
               data-testid="uat-filter-dev" style={{ maxWidth: 140 }} />
      </div>
      <div className="hw-uat-cycles__chips no-print">
        <button type="button"
                className={`hw-uat-cycles__chip${filter.failed_only ? " is-active" : ""}`}
                onClick={() => setFilter({...filter, failed_only: !filter.failed_only})}
                data-testid="uat-chip-failed">
          <XCircle className="w-3 h-3" /> Failed only
        </button>
        <button type="button"
                className={`hw-uat-cycles__chip${filter.critical_only ? " is-active" : ""}`}
                onClick={() => setFilter({...filter, critical_only: !filter.critical_only})}
                data-testid="uat-chip-critical">
          <AlertTriangle className="w-3 h-3" /> Critical only (S1/S2)
        </button>
        <button type="button"
                className={`hw-uat-cycles__chip${filter.retest_only ? " is-active" : ""}`}
                onClick={() => setFilter({...filter, retest_only: !filter.retest_only})}
                data-testid="uat-chip-retest">
          <RefreshCw className="w-3 h-3" /> Re-test required
        </button>
        <button type="button"
                className={`hw-uat-cycles__chip${filter.manual_only ? " is-active" : ""}`}
                onClick={() => setFilter({...filter, manual_only: !filter.manual_only})}
                title="Show only tests that require human judgment (hides runner_pending backlog)"
                data-testid="uat-chip-manual-only">
          <Users className="w-3 h-3" /> Manual only
          {current?.categorization?.manual_only != null && (
            <span className="hw-uat-cycles__chip-count">
              {current.categorization.manual_only}
            </span>
          )}
        </button>
        {(filter.failed_only || filter.critical_only || filter.retest_only ||
          filter.manual_only || filter.tester || filter.developer ||
          filter.status !== "all" || filter.severity !== "all" || filter.category !== "all" || filter.q) && (
          <button type="button" className="hw-uat-cycles__chip hw-uat-cycles__chip--clear"
                  onClick={() => setFilter({ status: "all", category: "all", severity: "all", q: "",
                                             tester: "", developer: "", critical_only: false,
                                             retest_only: false, failed_only: false, manual_only: false })}
                  data-testid="uat-chip-clear">
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
      </div>

      {/* Grouped test grid */}
      <div className="hw-uat-cycles__groups" data-testid="uat-test-grid">
        {Object.entries(groups).map(([cat, areas]) => (
          <div key={cat} className="hw-uat-cycles__group">
            <div className="hw-uat-cycles__group-title">{cat}</div>
            {Object.entries(areas).map(([area, tests]) => (
              <div key={area} className="hw-uat-cycles__area">
                <div className="hw-uat-cycles__area-title">{area}</div>
                <div className="hw-uat-cycles__rows">
                  {tests.map((t) => (
                    <TestRow key={t.test_id} test={t}
                             onOpen={(x) => setOpenTest(x)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
        {Object.keys(groups).length === 0 && (
          <div className="hw-uat-cycles__empty">No tests match current filters.</div>
        )}
      </div>

      {openTest && (
        <>
          <div className="hw-uat-cycles__scrim" onClick={() => setOpenTest(null)} />
          <TestDrawer cycleId={current.id} test={openTest}
                      isSignedOff={isSignedOff}
                      onClose={() => setOpenTest(null)}
                      onUpdated={onTestUpdated} />
        </>
      )}

      {showSignoff && (
        <SignOffDialog cycle={current} onClose={() => setShowSignoff(false)}
                       onDone={(c) => { setShowSignoff(false); setCurrent(c); onCycleChanged?.(c); }} />
      )}

      {showAutoExec && (
        <AutoExecuteDialog cycle={current}
                           onClose={() => { setShowAutoExec(false); if (autoExecResult) refresh(); }}
                           onDone={(res) => { setAutoExecResult(res); refresh(); }} />
      )}

      {showCoverage && (
        <CoverageReportDialog cycleId={current.id}
                              onClose={() => setShowCoverage(false)} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------
// Final Release Gate — release metadata, rollback runbook, backup verify
// ------------------------------------------------------------------
function ReleaseMetadataCard({ cycleId, meta, isSignedOff, onUpdated }) {
  const [form, setForm] = useState({
    app_version: meta?.app_version || "",
    git_commit: meta?.git_commit || "",
    deployment_target: meta?.deployment_target || "",
    release_notes: meta?.release_notes || "",
    notes_finalized: !!meta?.notes_finalized,
    monitoring_configured: !!meta?.monitoring_configured,
    audit_configured: !!meta?.audit_configured,
  });
  const [busy, setBusy] = useState(false);
  // Track whether user has unsaved edits so background re-renders can't
  // clobber the form state mid-typing (see Iter 55 testing report — race
  // condition between parent poll and this effect).
  const dirtyRef = useRef(false);
  const lastSyncedRef = useRef(meta?.updated_at || null);

  useEffect(() => {
    // Skip resync while the user is actively editing.
    if (dirtyRef.current) return;
    // Also skip if nothing about the server-side snapshot actually changed.
    if (meta?.updated_at && meta.updated_at === lastSyncedRef.current) return;
    setForm({
      app_version: meta?.app_version || "",
      git_commit: meta?.git_commit || "",
      deployment_target: meta?.deployment_target || "",
      release_notes: meta?.release_notes || "",
      notes_finalized: !!meta?.notes_finalized,
      monitoring_configured: !!meta?.monitoring_configured,
      audit_configured: !!meta?.audit_configured,
    });
    lastSyncedRef.current = meta?.updated_at || lastSyncedRef.current;
  }, [meta]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(
        `/admin/uat-cycles/${cycleId}/release-metadata`,
        form,
      );
      toast.success("Release metadata saved");
      // Form matches server now — allow future incoming resyncs.
      dirtyRef.current = false;
      lastSyncedRef.current = data.release_metadata?.updated_at || null;
      onUpdated(data.release_metadata, data.summary);
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const upd = (k) => (e) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  };

  return (
    <div className="hw-uat-cycles__gate-card" data-testid="uat-release-metadata">
      <div className="hw-uat-cycles__panel-header">
        <Rocket className="w-4 h-4" />
        <span>Release Metadata</span>
      </div>
      <div className="hw-uat-cycles__gate-grid">
        <label>
          <span>App version</span>
          <input type="text" value={form.app_version} onChange={upd("app_version")}
                 disabled={isSignedOff} placeholder="e.g. 30.55.0"
                 data-testid="uat-meta-app-version" />
        </label>
        <label>
          <span>Git commit</span>
          <input type="text" value={form.git_commit} onChange={upd("git_commit")}
                 disabled={isSignedOff} placeholder="short SHA"
                 data-testid="uat-meta-git-commit" />
        </label>
        <label>
          <span>Deployment target</span>
          <select value={form.deployment_target} onChange={upd("deployment_target")}
                  disabled={isSignedOff} data-testid="uat-meta-deployment-target">
            <option value="">— select —</option>
            <option value="production">production</option>
            <option value="staging">staging</option>
            <option value="sandbox">sandbox</option>
            <option value="other">other</option>
          </select>
        </label>
      </div>
      <label className="hw-uat-cycles__gate-full">
        <span>Release notes</span>
        <textarea rows={3} value={form.release_notes} onChange={upd("release_notes")}
                  disabled={isSignedOff} placeholder="What ships in this release, breaking changes, migrations…"
                  data-testid="uat-meta-release-notes" />
      </label>
      <div className="hw-uat-cycles__gate-flags">
        <label>
          <input type="checkbox" checked={form.notes_finalized} onChange={upd("notes_finalized")}
                 disabled={isSignedOff} data-testid="uat-meta-notes-finalized" />
          Release notes finalized
        </label>
        <label>
          <input type="checkbox" checked={form.monitoring_configured} onChange={upd("monitoring_configured")}
                 disabled={isSignedOff} data-testid="uat-meta-monitoring" />
          Monitoring configured
        </label>
        <label>
          <input type="checkbox" checked={form.audit_configured} onChange={upd("audit_configured")}
                 disabled={isSignedOff} data-testid="uat-meta-audit" />
          Audit trail configured
        </label>
      </div>
      <div className="hw-uat-cycles__gate-actions">
        <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                onClick={save} disabled={busy || isSignedOff}
                data-testid="uat-meta-save">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} <Save className="w-4 h-4" /> Save metadata
        </button>
      </div>
    </div>
  );
}

function RollbackRunbookCard({ cycleId, rb, isSignedOff, onUpdated }) {
  const [form, setForm] = useState({
    owner: rb?.owner || "",
    steps: rb?.steps || "",
    estimated_minutes: rb?.estimated_minutes ?? "",
    data_restore_steps: rb?.data_restore_steps || "",
    validation_steps: rb?.validation_steps || "",
    approved_by: rb?.approved_by || "",
    notes: rb?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const dirtyRef = useRef(false);
  const lastSyncedRef = useRef(rb?.updated_at || null);

  useEffect(() => {
    if (dirtyRef.current) return;
    if (rb?.updated_at && rb.updated_at === lastSyncedRef.current) return;
    setForm({
      owner: rb?.owner || "",
      steps: rb?.steps || "",
      estimated_minutes: rb?.estimated_minutes ?? "",
      data_restore_steps: rb?.data_restore_steps || "",
      validation_steps: rb?.validation_steps || "",
      approved_by: rb?.approved_by || "",
      notes: rb?.notes || "",
    });
    lastSyncedRef.current = rb?.updated_at || lastSyncedRef.current;
  }, [rb]);

  const upd = (k) => (e) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const save = async (markValidated) => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.estimated_minutes === "" || payload.estimated_minutes === null) {
        payload.estimated_minutes = 0;
      } else {
        payload.estimated_minutes = Number(payload.estimated_minutes);
      }
      if (markValidated !== undefined) payload.mark_validated = markValidated;
      const { data } = await api.patch(
        `/admin/uat-cycles/${cycleId}/rollback-runbook`,
        payload,
      );
      toast.success(markValidated ? "Rollback runbook validated" : "Rollback runbook saved");
      dirtyRef.current = false;
      lastSyncedRef.current = data.rollback_runbook?.updated_at || null;
      onUpdated(data.rollback_runbook, data.summary);
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally { setBusy(false); }
  };

  const validated = !!rb?.validated_at;
  return (
    <div className="hw-uat-cycles__gate-card" data-testid="uat-rollback-runbook">
      <div className="hw-uat-cycles__panel-header">
        <RotateCcw className="w-4 h-4" />
        <span>Rollback Runbook</span>
        {validated && (
          <span className="hw-uat-cycles__pill" style={{ color: "#065F46", background: "#D1FAE5" }}
                data-testid="uat-rollback-validated-pill">
            <CheckCircle2 className="w-3 h-3" /> Validated {relDate(rb.validated_at)}
          </span>
        )}
      </div>
      <div className="hw-uat-cycles__gate-grid">
        <label>
          <span>Rollback owner *</span>
          <input type="text" value={form.owner} onChange={upd("owner")}
                 disabled={isSignedOff} placeholder="Name / team"
                 data-testid="uat-rb-owner" />
        </label>
        <label>
          <span>Estimated minutes *</span>
          <input type="number" min="1" value={form.estimated_minutes}
                 onChange={upd("estimated_minutes")}
                 disabled={isSignedOff} placeholder="e.g. 20"
                 data-testid="uat-rb-minutes" />
        </label>
        <label>
          <span>Approved by *</span>
          <input type="text" value={form.approved_by} onChange={upd("approved_by")}
                 disabled={isSignedOff} placeholder="Approver name"
                 data-testid="uat-rb-approved-by" />
        </label>
      </div>
      <label className="hw-uat-cycles__gate-full">
        <span>Rollback steps *</span>
        <textarea rows={3} value={form.steps} onChange={upd("steps")}
                  disabled={isSignedOff} placeholder="Step-by-step rollback procedure…"
                  data-testid="uat-rb-steps" />
      </label>
      <label className="hw-uat-cycles__gate-full">
        <span>Data restore steps *</span>
        <textarea rows={2} value={form.data_restore_steps} onChange={upd("data_restore_steps")}
                  disabled={isSignedOff} placeholder="How to restore data from backup…"
                  data-testid="uat-rb-data-restore" />
      </label>
      <label className="hw-uat-cycles__gate-full">
        <span>Validation steps *</span>
        <textarea rows={2} value={form.validation_steps} onChange={upd("validation_steps")}
                  disabled={isSignedOff} placeholder="How to confirm rollback succeeded…"
                  data-testid="uat-rb-validation" />
      </label>
      <label className="hw-uat-cycles__gate-full">
        <span>Notes</span>
        <textarea rows={2} value={form.notes} onChange={upd("notes")}
                  disabled={isSignedOff} placeholder="Free-text notes…"
                  data-testid="uat-rb-notes" />
      </label>
      <div className="hw-uat-cycles__gate-actions">
        <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                onClick={() => save()} disabled={busy || isSignedOff}
                data-testid="uat-rb-save">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} <Save className="w-4 h-4" /> Save runbook
        </button>
        {validated ? (
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={() => save(false)} disabled={busy || isSignedOff}
                  data-testid="uat-rb-unvalidate">
            Unmark validated
          </button>
        ) : (
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                  onClick={() => save(true)} disabled={busy || isSignedOff}
                  data-testid="uat-rb-validate">
            <ShieldCheck className="w-4 h-4" /> Mark validated
          </button>
        )}
      </div>
    </div>
  );
}

function BackupVerifyCard({ cycleId, verifications, gate, isSignedOff, onUpdated }) {
  const [form, setForm] = useState({
    backup_timestamp: "",
    backup_location: "",
    notes: "",
    evidence_url: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.backup_timestamp.trim() || !form.backup_location.trim()) {
      toast.error("Backup timestamp and location are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/uat-cycles/${cycleId}/verify-backup`,
        form,
      );
      toast.success("Backup verification recorded");
      onUpdated(data.backup_verifications, data.summary);
      setForm({ backup_timestamp: "", backup_location: "", notes: "", evidence_url: "" });
    } catch (e) {
      toast.error(formatApiError(e, "Verification failed"));
    } finally { setBusy(false); }
  };

  const list = verifications || [];
  const recent = list.slice(-3).reverse();
  const ok = !!gate?.db_backup_verified_recent;

  return (
    <div className="hw-uat-cycles__gate-card" data-testid="uat-backup-verify">
      <div className="hw-uat-cycles__panel-header">
        <Database className="w-4 h-4" />
        <span>DB Backup Verification</span>
        <span className={`hw-uat-cycles__pill`}
              style={{ color: ok ? "#065F46" : "#991B1B", background: ok ? "#D1FAE5" : "#FEE2E2" }}
              data-testid="uat-backup-status-pill">
          {ok
            ? <><CheckCircle2 className="w-3 h-3" /> Verified within 24h ({gate?.last_backup_age_hours}h ago)</>
            : <><XCircle className="w-3 h-3" /> No recent verification</>}
        </span>
      </div>
      <div className="hw-uat-cycles__gate-grid">
        <label>
          <span>Backup timestamp *</span>
          <input type="text" value={form.backup_timestamp} onChange={upd("backup_timestamp")}
                 disabled={isSignedOff} placeholder="2026-07-02T00:00:00Z"
                 data-testid="uat-backup-ts" />
        </label>
        <label>
          <span>Backup location *</span>
          <input type="text" value={form.backup_location} onChange={upd("backup_location")}
                 disabled={isSignedOff} placeholder="s3://bucket/path or host:/dir"
                 data-testid="uat-backup-location" />
        </label>
        <label>
          <span>Evidence URL</span>
          <input type="text" value={form.evidence_url} onChange={upd("evidence_url")}
                 disabled={isSignedOff} placeholder="link to logs / dashboard"
                 data-testid="uat-backup-evidence" />
        </label>
      </div>
      <label className="hw-uat-cycles__gate-full">
        <span>Notes</span>
        <textarea rows={2} value={form.notes} onChange={upd("notes")}
                  disabled={isSignedOff} placeholder="Restore check details…"
                  data-testid="uat-backup-notes" />
      </label>
      <div className="hw-uat-cycles__gate-actions">
        <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                onClick={submit} disabled={busy || isSignedOff}
                data-testid="uat-backup-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} <Database className="w-4 h-4" /> Record verification
        </button>
      </div>
      {recent.length > 0 && (
        <div className="hw-uat-cycles__gate-history" data-testid="uat-backup-history">
          <div className="hw-uat-cycles__gate-history-title">Recent verifications</div>
          <ul>
            {recent.map((v) => (
              <li key={v.id} data-testid={`uat-backup-entry-${v.id}`}>
                <span className="hw-uat-cycles__gate-history-when">{relDate(v.verified_at)}</span>
                <span> — backup taken <code>{v.backup_timestamp}</code></span>
                <span> @ <code>{v.backup_location}</code></span>
                {v.notes && <div className="hw-uat-cycles__gate-history-notes">{v.notes}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FinalReleaseGatePanel({ cycle, isSignedOff, onCycleUpdate }) {
  const gate = cycle?.summary?.gate;
  const ok = !!gate?.ready_for_signoff;
  return (
    <div className={`hw-uat-cycles__panel hw-uat-cycles__gate-panel${ok ? " is-ok" : " is-blocked"}`}
         data-testid="uat-final-release-gate">
      <div className="hw-uat-cycles__panel-header">
        <ShieldAlert className="w-4 h-4" />
        <span>Final Release Gate</span>
        <span className="hw-uat-cycles__panel-count" data-testid="uat-gate-strict-count">
          {gate?.strict_checks_passed || 0} / {gate?.strict_checks_total || 12}
        </span>
        <span className={`hw-uat-cycles__pill`}
              style={{
                color: ok ? "#065F46" : "#991B1B",
                background: ok ? "#D1FAE5" : "#FEE2E2",
                marginLeft: "auto",
              }}
              data-testid="uat-gate-verdict-pill">
          {ok
            ? <><ShieldCheck className="w-3 h-3" /> Ready for sign-off</>
            : <><ShieldAlert className="w-3 h-3" /> Blocked</>}
        </span>
      </div>
      <GateBreakdown gate={gate} />
      <div className="hw-uat-cycles__gate-cards">
        <ReleaseMetadataCard
          cycleId={cycle.id}
          meta={cycle.release_metadata}
          isSignedOff={isSignedOff}
          onUpdated={(release_metadata, summary) => {
            onCycleUpdate({ release_metadata, summary });
          }} />
        <RollbackRunbookCard
          cycleId={cycle.id}
          rb={cycle.rollback_runbook}
          isSignedOff={isSignedOff}
          onUpdated={(rollback_runbook, summary) => {
            onCycleUpdate({ rollback_runbook, summary });
          }} />
        <BackupVerifyCard
          cycleId={cycle.id}
          verifications={cycle.backup_verifications}
          gate={gate}
          isSignedOff={isSignedOff}
          onUpdated={(backup_verifications, summary) => {
            onCycleUpdate({ backup_verifications, summary });
          }} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// Automation Coverage Report dialog (Iter 59)
// ------------------------------------------------------------------
function CoverageReportDialog({ cycleId, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/admin/uat-cycles/${cycleId}/coverage-report`);
        setReport(data);
      } catch (e) {
        toast.error(formatApiError(e, "Failed to load report"));
      } finally { setLoading(false); }
    })();
  }, [cycleId]);

  const pct = report?.automation_pct ?? 0;
  const ringColor = pct >= 95 ? "#059669" : pct >= 80 ? "#F59E0B" : "#DC2626";

  return (
    <>
      <div className="hw-uat-cycles__scrim" onClick={onClose} />
      <div className="hw-uat-cycles__dialog hw-uat-cycles__dialog--wide"
           role="dialog" data-testid="uat-coverage-dialog">
        <div className="hw-uat-cycles__dialog-header">
          <BarChart3 className="w-4 h-4" /> Automation Coverage Report
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-uat-cycles__dialog-body">
          {loading ? (
            <div className="hw-uat-cycles__auto-preview">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading report…
            </div>
          ) : !report ? (
            <div>No data.</div>
          ) : (
            <>
              <div className="hw-uat-cycles__cov-hero">
                <div className="hw-uat-cycles__cov-ring"
                     style={{ borderColor: ringColor, color: ringColor }}
                     data-testid="uat-coverage-pct">
                  {pct}%
                </div>
                <div className="hw-uat-cycles__cov-hero-stats">
                  <div>
                    <span className="hw-uat-cycles__cov-label">Total tests</span>
                    <strong data-testid="uat-coverage-total">{report.total}</strong>
                  </div>
                  <div>
                    <span className="hw-uat-cycles__cov-label">Automated</span>
                    <strong style={{ color: "#059669" }}
                            data-testid="uat-coverage-automated">
                      {report.automated}
                    </strong>
                  </div>
                  <div>
                    <span className="hw-uat-cycles__cov-label">Manual</span>
                    <strong style={{ color: "#7C3AED" }}
                            data-testid="uat-coverage-manual">
                      {report.manual}
                    </strong>
                  </div>
                  {report.pending > 0 && (
                    <div>
                      <span className="hw-uat-cycles__cov-label">Runner backlog</span>
                      <strong style={{ color: "#B45309" }}
                              data-testid="uat-coverage-pending">
                        {report.pending}
                      </strong>
                    </div>
                  )}
                </div>
              </div>
              <div className="hw-uat-cycles__cov-table" data-testid="uat-coverage-table">
                <div className="hw-uat-cycles__cov-table-head">
                  <span>Category</span>
                  <span>Automated</span>
                  <span>Manual</span>
                  <span>Coverage</span>
                </div>
                {report.categories.map((row) => (
                  <div key={row.category} className="hw-uat-cycles__cov-table-row"
                       data-testid={`uat-coverage-cat-${row.category.toLowerCase().replace(/\s+/g,'-')}`}>
                    <span className="hw-uat-cycles__cov-cat-name">{row.category}</span>
                    <span><strong>{row.automated}</strong> / {row.total}</span>
                    <span style={{ color: row.manual > 0 ? "#7C3AED" : "#94A3B8" }}>
                      {row.manual}
                    </span>
                    <span className="hw-uat-cycles__cov-bar-wrap"
                          title={`${row.automation_pct}%`}>
                      <span className="hw-uat-cycles__cov-bar"
                            style={{
                              width: `${row.automation_pct}%`,
                              background: row.automation_pct >= 95 ? "#059669"
                                        : row.automation_pct >= 80 ? "#F59E0B" : "#DC2626",
                            }} />
                      <span className="hw-uat-cycles__cov-bar-lbl">{row.automation_pct}%</span>
                    </span>
                  </div>
                ))}
              </div>
              {report.manual_only_ids?.length > 0 && (
                <div className="hw-uat-cycles__cov-manual" data-testid="uat-coverage-manual-list">
                  <div className="hw-uat-cycles__cov-manual-title">
                    Manual residual — genuine human-judgment tests
                  </div>
                  <ul>
                    {report.manual_only_ids.map((m) => (
                      <li key={m.test_id}>
                        <code>{m.test_id}</code>
                        {" "}<span className="hw-uat-cycles__cov-sev">[{m.severity}]</span>
                        {" "}{m.title}
                        {m.reason && <div className="hw-uat-cycles__cov-reason">{m.reason}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="hw-uat-cycles__cov-standard">
                <ShieldCheck className="w-4 h-4" />
                <div>
                  <strong>Engineering standard:</strong> every new feature
                  must ship with an automated UAT runner unless explicitly
                  documented as <code>manual_only</code>. See{" "}
                  <code>/app/docs/ENGINEERING_STANDARDS.md</code>.
                </div>
              </div>
            </>
          )}
        </div>
        <div className="hw-uat-cycles__dialog-footer">
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// Auto Execute dialog — kicks off runners for all Category-A tests
// still pending, and shows a summary of executed/passed/failed +
// runner_pending / manual_only counts.
// ------------------------------------------------------------------
function AutoExecuteDialog({ cycle, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(
          `/admin/uat-cycles/${cycle.id}/auto-execute`,
          { dry_run: true },
        );
        setPreview(data);
      } catch (e) {
        toast.error(formatApiError(e, "Preview failed"));
      } finally { setLoadingPreview(false); }
    })();
  }, [cycle.id]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(
        `/admin/uat-cycles/${cycle.id}/auto-execute`, {},
      );
      setResult(data);
      toast.success(`Auto-executed ${data.executed} tests`);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e, "Auto-execute failed"));
    } finally { setRunning(false); }
  };

  return (
    <>
      <div className="hw-uat-cycles__scrim" onClick={onClose} />
      <div className="hw-uat-cycles__dialog hw-uat-cycles__dialog--wide"
           role="dialog" data-testid="uat-auto-execute-dialog">
        <div className="hw-uat-cycles__dialog-header">
          <Zap className="w-4 h-4" /> Auto Execute — {cycle.release_name}
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-uat-cycles__dialog-body">
          {result ? (
            <div className="hw-uat-cycles__auto-result" data-testid="uat-auto-result">
              <div className="hw-uat-cycles__auto-headline">
                <CheckCircle2 className="w-5 h-5" style={{ color: "#059669" }} />
                Sweep complete
              </div>
              <div className="hw-uat-cycles__auto-metrics">
                <div className="hw-uat-cycles__auto-metric" data-testid="uat-auto-executed">
                  <div className="hw-uat-cycles__auto-metric-num">{result.executed}</div>
                  <div className="hw-uat-cycles__auto-metric-label">Executed</div>
                </div>
                <div className="hw-uat-cycles__auto-metric" style={{ color: "#059669" }}
                     data-testid="uat-auto-passed">
                  <div className="hw-uat-cycles__auto-metric-num">{result.passed}</div>
                  <div className="hw-uat-cycles__auto-metric-label">Passed</div>
                </div>
                <div className="hw-uat-cycles__auto-metric" style={{ color: "#DC2626" }}
                     data-testid="uat-auto-failed">
                  <div className="hw-uat-cycles__auto-metric-num">{result.failed}</div>
                  <div className="hw-uat-cycles__auto-metric-label">Failed</div>
                </div>
                <div className="hw-uat-cycles__auto-metric" style={{ color: "#B45309" }}
                     data-testid="uat-auto-skipped">
                  <div className="hw-uat-cycles__auto-metric-num">{result.skipped_not_pending}</div>
                  <div className="hw-uat-cycles__auto-metric-label">Skipped (already run)</div>
                </div>
                <div className="hw-uat-cycles__auto-metric" style={{ color: "#4B5563" }}
                     data-testid="uat-auto-runner-pending">
                  <div className="hw-uat-cycles__auto-metric-num">{result.runner_pending_count}</div>
                  <div className="hw-uat-cycles__auto-metric-label">Runner pending backlog</div>
                </div>
                <div className="hw-uat-cycles__auto-metric" style={{ color: "#7C3AED" }}
                     data-testid="uat-auto-manual">
                  <div className="hw-uat-cycles__auto-metric-num">{result.manual_only_count}</div>
                  <div className="hw-uat-cycles__auto-metric-label">Manual-only remaining</div>
                </div>
              </div>
              {result.failed > 0 && (
                <div className="hw-uat-cycles__auto-fail-list" data-testid="uat-auto-fail-list">
                  <div className="hw-uat-cycles__auto-fail-title">Failed tests (defects auto-created):</div>
                  <ul>
                    {result.results.filter((r) => r.new_status === "fail").map((r) => (
                      <li key={r.test_id} data-testid={`uat-auto-fail-${r.test_id}`}>
                        <strong>{r.test_id}</strong>
                        {r.defect_id && <span> — defect <code>{r.defect_id.slice(0,8)}</code></span>}
                        {r.error && <div className="hw-uat-cycles__auto-fail-err">{r.error}</div>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="hw-uat-cycles__auto-hint">
                Tip: enable the <strong>Manual only</strong> chip below to see the
                {" "}{result.manual_only_count} tests that still require your review.
                The {result.runner_pending_count} runner-pending tests are in the
                backlog for future runner authoring and don&rsquo;t need manual action.
              </div>
            </div>
          ) : loadingPreview ? (
            <div className="hw-uat-cycles__auto-preview">
              <Loader2 className="w-4 h-4 animate-spin" /> Analysing cycle…
            </div>
          ) : (
            <div className="hw-uat-cycles__auto-preview" data-testid="uat-auto-preview">
              <div className="hw-uat-cycles__auto-coverage" data-testid="uat-auto-coverage">
                <div className="hw-uat-cycles__auto-coverage-row">
                  <span>Runners registered globally</span>
                  <strong data-testid="uat-auto-runners-registered">
                    {preview?.runners_registered_global ?? "—"}
                  </strong>
                </div>
                <div className="hw-uat-cycles__auto-coverage-row">
                  <span>Discovered in this cycle</span>
                  <strong data-testid="uat-auto-runner-total">
                    {preview?.runner_available_total ?? 0}
                  </strong>
                </div>
                <div className="hw-uat-cycles__auto-coverage-row">
                  <span>Already executed</span>
                  <strong data-testid="uat-auto-runner-executed"
                          style={{ color: "#059669" }}>
                    {preview?.runner_available_executed ?? 0}
                  </strong>
                </div>
                <div className="hw-uat-cycles__auto-coverage-row">
                  <span>Ready to run now (pending)</span>
                  <strong data-testid="uat-auto-runner-pending-now"
                          style={{
                            color: (preview?.runner_available_pending || 0) > 0
                              ? "#1E40AF" : "#64748B",
                          }}>
                    {preview?.runner_available_pending ?? preview?.count ?? 0}
                  </strong>
                </div>
                <div className="hw-uat-cycles__auto-coverage-row">
                  <span>Runner-pending backlog (no runner yet)</span>
                  <strong data-testid="uat-auto-runner-backlog"
                          style={{ color: "#B45309" }}>
                    {preview?.runner_pending_count ?? 0}
                  </strong>
                </div>
                <div className="hw-uat-cycles__auto-coverage-row">
                  <span>Manual-only (human review)</span>
                  <strong data-testid="uat-auto-preview-manual"
                          style={{ color: "#7C3AED" }}>
                    {preview?.manual_only_count ?? 0}
                  </strong>
                </div>
              </div>
              {(preview?.count || 0) === 0 ? (
                <div className="hw-uat-cycles__auto-empty" data-testid="uat-auto-empty">
                  <CheckCircle2 className="w-4 h-4" style={{ color: "#059669" }} />
                  <div>
                    <strong>Nothing to auto-execute right now.</strong>
                    {" "}All {preview?.runner_available_total ?? 0} runner-ready
                    tests in this cycle have already been executed
                    ({preview?.runner_available_executed ?? 0} completed).
                    {" "}The remaining {preview?.manual_only_count ?? 0} manual
                    tests require your review.
                  </div>
                </div>
              ) : (
                <>
                  <p data-testid="uat-auto-preview-count-msg">
                    About to run <strong data-testid="uat-auto-preview-count">
                    {preview?.count || 0}</strong> automated runners against
                    pending tests. Already-executed tests will be
                    {" "}<strong>skipped</strong> (no overwrites).
                  </p>
                  {preview?.candidates?.length > 0 && (
                    <details className="hw-uat-cycles__auto-preview-details">
                      <summary>Tests that will be executed</summary>
                      <ul>
                        {preview.candidates.map((id) => <li key={id}><code>{id}</code></li>)}
                      </ul>
                    </details>
                  )}
                </>
              )}
              <div className="hw-uat-cycles__auto-safety">
                <ShieldCheck className="w-4 h-4" />
                <div>
                  <strong>Safety:</strong> Runners use auto-provisioned test
                  accounts. Evidence (HTTP request/response) is captured for
                  every execution. Any failure will auto-open a defect for
                  triage. Already-executed statuses are never overwritten.
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="hw-uat-cycles__dialog-footer">
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={onClose}>Close</button>
          {!result && (
            <button type="button"
                    className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                    onClick={run} disabled={running || loadingPreview || !preview?.count}
                    data-testid="uat-auto-execute-confirm">
              {running && <Loader2 className="w-4 h-4 animate-spin" />}
              <Zap className="w-4 h-4" /> Run {preview?.count || 0} auto-checks
            </button>
          )}
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// Sign-off dialog
// ------------------------------------------------------------------
function SignOffDialog({ cycle, onClose, onDone }) {
  const [name, setName] = useState("");
  const [decision, setDecision] = useState("approved");
  const [conditions, setConditions] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your name for signature."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/uat-cycles/${cycle.id}/sign-off`, {
        signature_name: name.trim(), decision, conditions: conditions.trim() || null,
      });
      toast.success(`Cycle ${decision.replace(/_/g," ")}`);
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e, "Sign-off failed"));
    } finally { setBusy(false); }
  };
  return (
    <>
      <div className="hw-uat-cycles__scrim" onClick={onClose} />
      <div className="hw-uat-cycles__dialog" role="dialog" data-testid="uat-signoff-dialog">
        <div className="hw-uat-cycles__dialog-header">
          <ShieldCheck className="w-4 h-4" /> UAT Sign-Off — {cycle.release_name}
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-uat-cycles__dialog-body">
          <div className="hw-uat-cycles__row-group">
            <label htmlFor="signoff-name">Signature (your full name)</label>
            <input id="signoff-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Eric" data-testid="uat-signoff-name" />
          </div>
          <div className="hw-uat-cycles__row-group">
            <label>Decision</label>
            <div className="hw-uat-cycles__status-group">
              {["approved","approved_with_conditions","rejected"].map(d => (
                <button key={d} type="button"
                        className={`hw-uat-cycles__status-btn${decision === d ? " is-active" : ""}`}
                        onClick={() => setDecision(d)}
                        data-testid={`uat-signoff-decision-${d}`}>{d.replace(/_/g," ")}</button>
              ))}
            </div>
          </div>
          {(decision === "approved_with_conditions" || decision === "rejected") && (
            <div className="hw-uat-cycles__row-group">
              <label htmlFor="signoff-cond">{decision === "rejected" ? "Reason" : "Conditions"}</label>
              <textarea id="signoff-cond" rows={3} value={conditions}
                        onChange={(e) => setConditions(e.target.value)}
                        data-testid="uat-signoff-conditions" />
            </div>
          )}
        </div>
        <div className="hw-uat-cycles__dialog-footer">
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={onClose}>Cancel</button>
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                  onClick={submit} disabled={busy}
                  data-testid="uat-signoff-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirm sign-off
          </button>
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// New cycle dialog
// ------------------------------------------------------------------
function NewCycleDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Release name required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/uat-cycles", {
        release_name: name.trim(),
        release_version: version.trim() || null,
      });
      toast.success(`Created cycle with ${data.summary.total} tests`);
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e, "Create failed"));
    } finally { setBusy(false); }
  };
  return (
    <>
      <div className="hw-uat-cycles__scrim" onClick={onClose} />
      <div className="hw-uat-cycles__dialog" data-testid="uat-new-cycle-dialog">
        <div className="hw-uat-cycles__dialog-header">
          <ClipboardCheck className="w-4 h-4" /> New UAT Cycle
          <button type="button" className="hw-uat-cycles__icon-btn" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hw-uat-cycles__dialog-body">
          <div className="hw-uat-cycles__row-group">
            <label htmlFor="new-cycle-name">Release name</label>
            <input id="new-cycle-name" type="text" value={name}
                   onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Governance & Compliance Release"
                   data-testid="uat-new-cycle-name" />
          </div>
          <div className="hw-uat-cycles__row-group">
            <label htmlFor="new-cycle-ver">Version (optional)</label>
            <input id="new-cycle-ver" type="text" value={version}
                   onChange={(e) => setVersion(e.target.value)}
                   placeholder="e.g. 1.0" data-testid="uat-new-cycle-ver" />
          </div>
          <div className="hw-uat-cycles__hint">
            Tests will be parsed from <code>uat/08_UAT_Checklist.md</code> — the current release&rsquo;s
            authoritative checklist. This snapshot lives with the cycle forever.
          </div>
        </div>
        <div className="hw-uat-cycles__dialog-footer">
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--ghost"
                  onClick={onClose}>Cancel</button>
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                  onClick={submit} disabled={busy}
                  data-testid="uat-new-cycle-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create cycle
          </button>
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------------
// Main exported component
// ------------------------------------------------------------------
export default function AdminUATCycles() {
  const [cycles, setCycles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadCycles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/uat-cycles");
      setCycles(data.cycles);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load cycles"));
    } finally { setLoading(false); }
  }, []);

  const loadOne = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/admin/uat-cycles/${id}`);
      setSelected(data);
    } catch (e) { toast.error(formatApiError(e, "Failed to load cycle")); }
  }, []);

  useEffect(() => { loadCycles(); }, [loadCycles]);

  if (selected) {
    return (
      <CycleDetail cycle={selected}
                   onBack={() => { setSelected(null); loadCycles(); }}
                   onCycleChanged={(c) => setSelected(c)} />
    );
  }

  return (
    <div className="hw-uat-cycles" data-testid="uat-cycles-root">
      <div className="hw-uat-cycles__header no-print">
        <div>
          <h2>UAT Cycles</h2>
          <p className="hw-uat-cycles__subtitle">
            Interactive test execution + permanent testing history. One cycle per release.
          </p>
        </div>
        <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                onClick={() => setShowNew(true)} data-testid="uat-new-cycle-btn">
          <Plus className="w-4 h-4" /> New cycle
        </button>
      </div>

      {loading ? (
        <div className="hw-uat-cycles__loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (cycles?.length ?? 0) === 0 ? (
        <div className="hw-uat-cycles__empty">
          <ClipboardCheck className="w-6 h-6" />
          <p>No UAT cycles yet.</p>
          <button type="button" className="hw-uat-cycles__btn hw-uat-cycles__btn--primary"
                  onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4" /> Start the first cycle
          </button>
        </div>
      ) : (
        <div className="hw-uat-cycles__list" data-testid="uat-cycles-list">
          {cycles.map((c) => (
            <button key={c.id} type="button" className="hw-uat-cycles__card"
                    onClick={() => loadOne(c.id)}
                    data-testid={`uat-cycle-card-${c.id}`}>
              <div className="hw-uat-cycles__card-title">
                <FileText className="w-4 h-4" /> {c.release_name}
                {c.release_version && <span className="hw-uat-cycles__card-ver">v{c.release_version}</span>}
              </div>
              <div className="hw-uat-cycles__card-meta">
                Created {relDate(c.created_at)} · {c.summary?.total || 0} tests · {c.summary?.completion_pct || 0}% complete
              </div>
              <div className="hw-uat-cycles__card-progress">
                <div className="hw-uat-cycles__progress">
                  <div className="hw-uat-cycles__progress-bar" style={{ width: `${c.summary?.completion_pct || 0}%` }} />
                </div>
                <span className={`hw-uat-cycles__card-status hw-uat-cycles__card-status--${c.status}`}>
                  {c.status.replace(/_/g," ")}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showNew && (
        <NewCycleDialog onClose={() => setShowNew(false)}
                        onCreated={(c) => { setShowNew(false); setSelected(c); loadCycles(); }} />
      )}
    </div>
  );
}
